import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface User {
  id: string
  tenant_id: string
  email: string
  phone?: string | null
  roleId: string
  isActive: boolean
  lastLoginAt?: string | null
  createdAt: string
  role?: {
    id: string
    name: string
    permissions: Record<string, any>
  }
  employee?: {
    id: string
    fullName: string
  } | null
}

const USERS_QUERY_KEY = 'users'

export const useUsers = () => {
  return useQuery({
    queryKey: [USERS_QUERY_KEY],
    queryFn: async (): Promise<User[]> => {
      const response = await apiClient.getUsers()
      // Backend returns array directly (not paginated)
      if (Array.isArray(response)) return response
      return response.data || []
    },
    staleTime: 5 * 60 * 1000,
    retry: 3,
  })
}

export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { email: string; password: string; phone?: string; roleId: string }) => {
      return await apiClient.createUser(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
    },
  })
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { email?: string; phone?: string | null; roleId?: string; isActive?: boolean } }) => {
      return await apiClient.updateUser(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
    },
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteUser(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
    },
  })
}

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async ({ userId, newPassword }: { userId: string; newPassword: string }) => {
      return await apiClient.resetUserPassword(userId, newPassword)
    },
  })
}
